import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import api from '../../api/axios';
import openPdf from '../../api/openPdf';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';

const statusConfig = {
  open:        { label: 'Abierto',    bg: '#DBEAFE', color: '#1D4ED8' },
  in_progress: { label: 'En proceso', bg: '#FEF3C7', color: '#92400E' },
  completed:   { label: 'Completado', bg: '#D1FAE5', color: '#065F46' },
  cancelled:   { label: 'Cancelado',  bg: '#FEE2E2', color: '#991B1B' },
};

const Badge = ({ status }) => {
  const c = statusConfig[status] || { label: status, bg: '#F3F4F6', color: '#374151' };
  return (
    <span style={{
      display: 'inline-flex', padding: '3px 9px', borderRadius: 20,
      fontSize: 11, fontWeight: 600, backgroundColor: c.bg, color: c.color, whiteSpace: 'nowrap',
    }}>
      {c.label}
    </span>
  );
};

const inputBase = {
  padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8,
  fontSize: 13, color: '#374151', backgroundColor: '#F9FAFB', outline: 'none', cursor: 'pointer',
};

// ── Paginación inteligente con "..." ─────────────────────────────────────────
function buildPageRange(current, last) {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
  const pages = new Set([1, last, current, current - 1, current + 1].filter(p => p >= 1 && p <= last));
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  let prev = null;
  for (const p of sorted) {
    if (prev !== null && p - prev > 1) result.push('...');
    result.push(p);
    prev = p;
  }
  return result;
}

const CaseFileList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [caseFiles, setCaseFiles]       = useState([]);
  const [pagination, setPagination]     = useState({});
  const [procedureTypes, setProcedureTypes] = useState([]);
  const [users, setUsers]               = useState([]);
  const [staleDays, setStaleDays]       = useState(7);
  const [loading, setLoading]           = useState(true);
  const [filters, setFilters]           = useState({
    status: searchParams.get('status') || '',
    procedure_type_id: searchParams.get('procedure_type_id') || '',
    assigned_user_id: searchParams.get('assigned_user_id') || '',
    search: searchParams.get('search') || '',
    stale: searchParams.get('stale') || '',
  });
  const [currentPage, setCurrentPage]   = useState(1);
  const [hoveredRow, setHoveredRow]     = useState(null);
  const [exportHover, setExportHover]   = useState(false);
  const ptRef   = useRef([]);
  const usersRef = useRef([]);

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [cfRes, ptRes, usersRes, settingsRes] = await Promise.all([
        api.get('/case-files', { params }),
        ptRef.current.length   ? Promise.resolve({ data: ptRef.current })    : api.get('/procedure-types'),
        usersRef.current.length ? Promise.resolve({ data: usersRef.current }) : api.get('/users'),
        api.get('/settings/system'),
      ]);
      setCaseFiles(cfRes.data.data);
      setPagination(cfRes.data);
      if (!ptRef.current.length)    { ptRef.current = ptRes.data;      setProcedureTypes(ptRes.data); }
      if (!usersRef.current.length) { usersRef.current = usersRes.data; setUsers(usersRes.data); }
      const s = settingsRes.data.find(s => s.key === 'stale_case_file_days');
      if (s) setStaleDays(parseInt(s.value));
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar los expedientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setCurrentPage(1); fetchData(1); }, [filters]); // eslint-disable-line

  const setFilter   = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => setFilters({ status: '', procedure_type_id: '', assigned_user_id: '', search: '', stale: '' });

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const hasActiveFilters  = activeFilterCount > 0;

  const handleDelete = async (e, id) => {
    e.stopPropagation(); // no navegar al detail
    toast((t) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontFamily: 'sans-serif' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>¿Eliminar este expediente?</span>
        <span style={{ fontSize: 12, color: '#6B7280' }}>Esta acción no se puede deshacer.</span>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={() => toast.dismiss(t.id)}
            style={{ padding: '5px 12px', borderRadius: 6, border: '1.5px solid #E5E7EB', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>
            Cancelar
          </button>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                await api.delete(`/case-files/${id}`);
                toast.success('Expediente eliminado');
                fetchData(currentPage);
              } catch {
                toast.error('Error al eliminar el expediente');
              }
            }}
            style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#EF4444', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#fff' }}>
            Eliminar
          </button>
        </div>
      </div>
    ), { duration: 8000 });
  };

  const handleExportPdf = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    openPdf('/api/case-files/pdf/list', params);
  };

  return (
    <div>
      <PageHeader
        title={
          <>
            Expedientes
            {pagination.total > 0 && (
              <span style={{ fontSize: 13, fontWeight: 600, padding: '2px 9px', borderRadius: 20, backgroundColor: '#EEF0FB', color: '#4F63D2', marginLeft: 8 }}>
                {pagination.total}
              </span>
            )}
            {/* Indicador de filtros activos */}
            {hasActiveFilters && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, backgroundColor: '#FEF3C7', color: '#92400E', marginLeft: 6, border: '1px solid #FDE68A' }}>
                {activeFilterCount} filtro{activeFilterCount > 1 ? 's' : ''} activo{activeFilterCount > 1 ? 's' : ''}
              </span>
            )}
          </>
        }
        subtitle="Gestiona los expedientes de la notaría"
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Exportar PDF */}
            <button
              onClick={handleExportPdf}
              onMouseEnter={() => setExportHover(true)}
              onMouseLeave={() => setExportHover(false)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 8,
                border: '1.5px solid #E5E7EB',
                backgroundColor: exportHover ? '#F9FAFB' : '#fff',
                color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                transition: 'background 0.15s',
              }}>
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Exportar PDF
            </button>
            {/* Nuevo Expediente */}
            <Link
              to="/case-files/create"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 8, border: 'none',
                backgroundColor: '#4F63D2', color: '#fff',
                fontWeight: 600, fontSize: 13, textDecoration: 'none',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#3d4fb8'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#4F63D2'}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
              Nuevo Expediente
            </Link>
          </div>
        }
      />

      {/* ── Filtros ── */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>

          {/* Búsqueda */}
          <div style={{ position: 'relative', flex: '1', minWidth: 200 }}>
            <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 15, height: 15, color: '#9CA3AF', pointerEvents: 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Buscar por título o cliente..."
              value={filters.search}
              onChange={e => setFilter('search', e.target.value)}
              style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#111827', backgroundColor: '#F9FAFB', outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => { e.target.style.borderColor = '#4F63D2'; e.target.style.backgroundColor = '#fff'; }}
              onBlur={e => { e.target.style.borderColor = '#E5E7EB'; e.target.style.backgroundColor = '#F9FAFB'; }}
            />
          </div>

          {/* Estado */}
          <select value={filters.status} onChange={e => setFilter('status', e.target.value)} style={inputBase}>
            <option value="">Todos los estados</option>
            <option value="open">Abierto</option>
            <option value="in_progress">En proceso</option>
            <option value="completed">Completado</option>
            <option value="cancelled">Cancelado</option>
          </select>

          {/* Tipo de trámite */}
          <select value={filters.procedure_type_id} onChange={e => setFilter('procedure_type_id', e.target.value)} style={inputBase}>
            <option value="">Todos los trámites</option>
            {procedureTypes.map(pt => <option key={pt.id} value={pt.id}>{pt.name}</option>)}
          </select>

          {/* Responsable */}
          <select value={filters.assigned_user_id} onChange={e => setFilter('assigned_user_id', e.target.value)} style={inputBase}>
            <option value="">Todos los responsables</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>

          {/* Toggle "Sin actividad" — pill button */}
          <button
            onClick={() => setFilter('stale', filters.stale === '1' ? '' : '1')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
              fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
              border: `1.5px solid ${filters.stale === '1' ? '#F59E0B' : '#E5E7EB'}`,
              backgroundColor: filters.stale === '1' ? '#FEF3C7' : '#F9FAFB',
              color: filters.stale === '1' ? '#92400E' : '#6B7280',
              transition: 'all 0.15s',
            }}
          >
            ⚠ Sin actividad (+{staleDays}d)
          </button>

          {/* Limpiar filtros */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
                fontSize: 12, fontWeight: 600,
                border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#6B7280',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#9CA3AF'; e.currentTarget.style.color = '#374151'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.color = '#6B7280'; }}
            >
              <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* ── Tabla ── */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, flexDirection: 'column', gap: 12 }}>
            <div style={{ width: 32, height: 32, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
            <span style={{ fontSize: 13, color: '#9CA3AF' }}>Cargando expedientes...</span>
          </div>
        ) : caseFiles.length === 0 ? (
          hasActiveFilters ? (
            /* EmptyState contextual cuando hay filtros activos */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', gap: 12 }}>
              <span style={{ fontSize: 36 }}>🔍</span>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', fontFamily: 'sans-serif' }}>Sin resultados para estos filtros</div>
              <div style={{ fontSize: 13, color: '#6B7280', fontFamily: 'sans-serif', textAlign: 'center', maxWidth: 340 }}>
                Ningún expediente coincide con los filtros aplicados. Prueba ajustando la búsqueda o limpiando los filtros.
              </div>
              <button
                onClick={clearFilters}
                style={{
                  marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 18px', borderRadius: 8, border: '1.5px solid #E5E7EB',
                  background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'sans-serif', transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#F9FAFB'; e.currentTarget.style.borderColor = '#9CA3AF'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
              >
                <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Limpiar todos los filtros
              </button>
            </div>
          ) : (
            <EmptyState icon="📂" title="No hay expedientes registrados" subtitle="Los expedientes se crean desde el perfil del cliente o con el botón Nuevo Expediente" />
          )
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB' }}>
                  {['', 'Cliente', 'Expediente', 'Trámite', 'Responsable', 'Docs', 'Tiempo', 'Estado', ''].map((h, i) => (
                    <th
                      key={i}
                      className={[3, 4, 5, 6].includes(i) ? 'mobile-hide' : ''}
                      style={{ padding: '10px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF', textAlign: 'left', borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {caseFiles.map(cf => {
                  const isHovered = hoveredRow === cf.id;
                  const docStats  = cf.document_stats;
                  const docsOk    = docStats?.total > 0 && docStats?.delivered === docStats?.total;
                  const docsLabel = docStats?.total > 0 ? `${docStats.delivered}/${docStats.total}` : '—';

                  return (
                    <tr
                      key={cf.id}
                      onClick={() => navigate(`/case-files/${cf.id}`)}
                      onMouseEnter={() => setHoveredRow(cf.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                      style={{
                        backgroundColor: isHovered ? '#FAFBFF' : cf.is_stale ? '#FFFBF0' : '#fff',
                        cursor: 'pointer',
                        transition: 'background 0.1s',
                      }}
                    >
                      {/* Alerta stale */}
                      <td style={{ padding: '10px 8px 10px 14px', width: 28 }}>
                        {cf.is_stale && (
                          <span title={`Sin actividad por más de ${staleDays} días`}>⚠️</span>
                        )}
                      </td>

                      {/* Cliente */}
                      <td style={{ padding: '10px 14px' }}>
                        <span
                          onClick={e => { e.stopPropagation(); navigate(`/clients/${cf.client_id}`); }}
                          style={{ fontSize: 13, fontWeight: 600, color: '#4F63D2', cursor: 'pointer', textDecoration: isHovered ? 'underline' : 'none' }}
                        >
                          {cf.client?.name}
                        </span>
                        <div className="d-md-none" style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{cf.title}</div>
                      </td>

                      {/* Título */}
                      <td className="mobile-hide" style={{ padding: '10px 14px', fontSize: 13, color: '#374151', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cf.title}
                      </td>

                      {/* Trámite */}
                      <td className="mobile-hide" style={{ padding: '10px 14px', fontSize: 12, color: '#6B7280' }}>
                        {cf.procedure_type?.name || <span style={{ color: '#D1D5DB' }}>—</span>}
                      </td>

                      {/* Responsable */}
                      <td className="mobile-hide" style={{ padding: '10px 14px', fontSize: 12, color: '#6B7280' }}>
                        {cf.assigned_user?.name || <span style={{ color: '#EF4444', fontWeight: 600 }}>Sin asignar</span>}
                      </td>

                      {/* Docs */}
                      <td className="mobile-hide" style={{ padding: '10px 14px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                          backgroundColor: docsOk ? '#D1FAE5' : docStats?.total > 0 ? '#FEF3C7' : '#F3F4F6',
                          color: docsOk ? '#065F46' : docStats?.total > 0 ? '#92400E' : '#9CA3AF',
                        }}>
                          {docsLabel}
                        </span>
                      </td>

                      {/* Tiempo — oculto en mobile */}
                      <td className="mobile-hide" style={{ padding: '10px 14px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                          backgroundColor: cf.is_stale ? '#FEE2E2' : '#F3F4F6',
                          color: cf.is_stale ? '#991B1B' : '#374151',
                        }}>
                          {cf.elapsed_time}
                        </span>
                      </td>

                      {/* Estado */}
                      <td style={{ padding: '10px 14px' }}>
                        <Badge status={cf.status} />
                      </td>

                      {/* Acciones */}
                      <td style={{ padding: '10px 14px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 5, opacity: isHovered ? 1 : 0, transition: 'opacity 0.15s' }}>
                          {/* Ver */}
                          <Link
                            to={`/case-files/${cf.id}`}
                            title="Ver expediente"
                            onClick={e => e.stopPropagation()}
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 7, backgroundColor: '#EEF0FB', color: '#4F63D2', textDecoration: 'none' }}
                          >
                            <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </Link>
                          {/* Eliminar */}
                          <button
                            title="Eliminar expediente"
                            onClick={e => handleDelete(e, cf.id)}
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 7, backgroundColor: '#FEE2E2', color: '#EF4444', border: 'none', cursor: 'pointer' }}
                          >
                            <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* ── Paginación inteligente ── */}
            {pagination.last_page > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #F3F4F6', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#9CA3AF' }}>
                  Mostrando {pagination.from}–{pagination.to} de {pagination.total} expedientes
                </span>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  {/* Anterior */}
                  <button
                    disabled={currentPage === 1}
                    onClick={() => { if (currentPage > 1) { setCurrentPage(p => p - 1); fetchData(currentPage - 1); } }}
                    style={{ minWidth: 30, height: 30, borderRadius: 7, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: currentPage === 1 ? '#D1D5DB' : '#374151', fontSize: 13, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', padding: '0 6px' }}
                  >
                    ‹
                  </button>

                  {/* Números con "..." */}
                  {buildPageRange(currentPage, pagination.last_page).map((item, i) =>
                    item === '...' ? (
                      <span key={`ellipsis-${i}`} style={{ minWidth: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#9CA3AF' }}>
                        …
                      </span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => { setCurrentPage(item); fetchData(item); }}
                        style={{ minWidth: 30, height: 30, borderRadius: 7, border: '1.5px solid', borderColor: currentPage === item ? '#4F63D2' : '#E5E7EB', backgroundColor: currentPage === item ? '#4F63D2' : '#fff', color: currentPage === item ? '#fff' : '#374151', fontWeight: currentPage === item ? 700 : 400, fontSize: 13, cursor: 'pointer', padding: '0 6px' }}
                      >
                        {item}
                      </button>
                    )
                  )}

                  {/* Siguiente */}
                  <button
                    disabled={currentPage === pagination.last_page}
                    onClick={() => { if (currentPage < pagination.last_page) { setCurrentPage(p => p + 1); fetchData(currentPage + 1); } }}
                    style={{ minWidth: 30, height: 30, borderRadius: 7, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: currentPage === pagination.last_page ? '#D1D5DB' : '#374151', fontSize: 13, cursor: currentPage === pagination.last_page ? 'not-allowed' : 'pointer', padding: '0 6px' }}
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CaseFileList;
